import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { CORE_CONFIG, CoreConfig } from '../../models/core-config/core-config.interface';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  // apis provided from environment
  apis: Array<ApiItem> = new Array();

  constructor(@Inject(CORE_CONFIG) private config: CoreConfig, private http: HttpClient) {
    this.apis = config.apis;
  }

  /**
   * Retrieve an api from id.
   * @param id - api Id.
   */

  getApi(id: string): ApiItem {
    return this.apis.find((apiObject: any) => apiObject.id === id);
  }

  /**
   * Prepare query args.
   * @param api - api id.
   * @param query - query params.
   */
  prepareQuery(api: any, query: string): string {
    return api ? `${api.url}${query}` : query;
  }

  /**
   * Prepare options method.
   * @param api - api id
   * @param options - options.
   */
  prepareOptions(api: any, options: any = {}): object {
    return api ? Object.assign({}, options, api.options) : options;
  }

  /**
   * Xhr Get method
   * @param apiName - api environment key
   * @param url - endpoint url
   * @param options - request options
   */
  get<T>(apiName: string, url: string, options?: any): Observable<T> {
    const api = this.getApi(apiName);
    return this.http.get<T>(this.prepareQuery(api, url), this.prepareOptions(api, options));
  }

  /**
   * Xhr post method
   * @param apiName - api environment key
   * @param url - endpoint url
   * @param data - request data
   * @param options - request options
   */
  post<T>(apiName: string, url: string, data: any, options?: any): Observable<T> {
    const api = this.getApi(apiName);
    return this.http.post<T>(this.prepareQuery(api, url), data, this.prepareOptions(api, options));
  }

  /**
   * Xhr put method
   * @param apiName - api environment key
   * @param url - endpoint url
   * @param data - request data
   * @param options - request options
   */
  put<T>(apiName: string, url: string, data: any, options?: any): Observable<T> {
    const api = this.getApi(apiName);
    return this.http.put<T>(this.prepareQuery(api, url), data, this.prepareOptions(api, options));
  }

  /**
   * Xhr patch method
   * @param apiName - api environment key
   * @param url - endpoint url
   * @param data - request data
   * @param options - request options
   */
  patch<T>(apiName: string, url: string, data: any, options?: any): Observable<T> {
    const api = this.getApi(apiName);
    return this.http.patch<T>(this.prepareQuery(api, url), data, this.prepareOptions(api, options));
  }

  /**
   * Xhr delete method
   * @param apiName - api environment key
   * @param url - endpoint url
   * @param options - request options
   */
  delete<T>(apiName: string, url: string, options?: any): Observable<T> {
    const api = this.getApi(apiName);
    return this.http.delete<T>(this.prepareQuery(api, url), this.prepareOptions(api, options));
  }
}

export interface ApiItem {
  id: string;
  url: string;
  options?: any;
}
